import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { Link } from 'react-router';
import { EmptyState, noRecords } from '../empty-state';
import { sortRows } from './sort';

export interface Column<Data> {
  sortable?: boolean;
  isDefaultSortColumn?: boolean;
  id: keyof Data;
  label?: string;
  numeric?: boolean;
  renderer?: (row: Data) => React.ReactNode;
  getURL?: (row: Data) => string;
  sortValue?: (row: Data) => string | number | null | undefined;
}

export type Order = 'asc' | 'desc';

interface TableProps<Data> {
  columns: readonly Column<Data>[];
  rows: Array<Data>;
  loading?: boolean;
  order?: Order;
  orderBy?: keyof Data;
  onRequestSort: (property: keyof Data) => void;
  rowsPerPage?: number;
  page?: number;
  setPage: (newPage: number) => void;
  setRowsPerPage: (rows: number) => void;
}

export function Table<Data>({
  rows,
  columns,
  loading,
  order,
  orderBy,
  onRequestSort,
  rowsPerPage = 100,
  page = 0,
  setPage,
  setRowsPerPage,
}: TableProps<Data>): JSX.Element {
  const createSortHandler = (property: keyof Data) => () => {
    onRequestSort(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedRows = sortRows(rows, columns, orderBy, order);
  const visibleRows = sortedRows.slice(
    (page || 0) * rowsPerPage,
    (page || 0) * rowsPerPage + rowsPerPage,
  );
  const rowsPerPageOptions = [10, 50, 100];
  if (!rowsPerPageOptions.includes(rowsPerPage)) {
    rowsPerPageOptions.push(rowsPerPage);
  }

  return (
    <Paper>
      <TableContainer>
        <MuiTable aria-labelledby="tableTitle">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  variant="head"
                  key={column.id as string}
                  align={column.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={createSortHandler(column.id)}
                    >
                      {column.label}
                      {orderBy === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.map((row) => (
              <TableRow tabIndex={-1} key={row['id']} hover>
                {columns.map((column) => {
                  let content = column.renderer ? column.renderer(row) : row[column.id];
                  const url = column.getURL?.(row);
                  if (url) {
                    content = <Link to={url}>{content as React.ReactNode}</Link>;
                  }
                  return (
                    <TableCell
                      key={`row-${String(row['id'])}-cell-${String(column.id)}`}
                      component="td"
                      scope="row"
                      align={column.numeric ? 'right' : 'left'}
                    >
                      {content as React.ReactNode}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {loading && (
        <Box display="flex" justifyContent="center" sx={{ paddingY: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && !rows?.length && (
        <Box sx={{ paddingY: 8 }}>
          <EmptyState>
            <EmptyState.Image src={noRecords} alt="No Records" />
          </EmptyState>
        </Box>
      )}
      {!loading && !!rows.length && !visibleRows.length && (
        <Box sx={{ paddingY: 8 }}>
          <EmptyState>
            <EmptyState.Image src={noRecords} alt="No Results" />
          </EmptyState>
        </Box>
      )}
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions.sort((a, b) => a - b)}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}
